/** Claim detail (spec §8.9): header, details, timeline, documents, reminders. */

import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CircleDot,
  FileCheck2,
  FilePlus2,
  MessageSquare,
  Pencil,
  PhoneCall,
  RefreshCw,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input, Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/misc';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EnumBadge, useEnumOptions } from '@/components/common/enum-badge';
import { DocumentPanel } from '@/components/common/document-panel';
import { EmptyState, Field, FieldGrid } from '@/components/common/page-header';
import {
  DetailHeader,
  DetailMetrics,
  DetailPage,
  DetailPageSkeleton,
  DetailTabsBar,
  Metric,
  Section,
  SectionStack,
  Workspace,
} from '@/components/common/detail-page';
import { EntityAvatar } from '@/components/common/entity-avatar';
import { PolicyRef } from '@/components/common/policy-ref';
import { claimsApi, useChangeClaimStatus, useClaimTimeline } from '@/lib/api/domains';
import { useOfflineMutation, PENDING_FLAG } from '@/lib/offline/use-offline-mutation';
import { PendingSyncBadge, RequiresConnection } from '@/components/offline/offline-indicator';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';

import { ClaimFormDialog } from './claim-form';

/** Timeline icon per action type — keeps the registry as the only enum knowledge. */
const TIMELINE_ICONS: Record<string, LucideIcon> = {
  created: FilePlus2,
  status_changed: RefreshCw,
  document_uploaded: FileCheck2,
  note_added: MessageSquare,
  contact_insurer: PhoneCall,
  payout_received: Wallet,
  follow_up: CircleDot,
};

export function ClaimDetailPage() {
  const { id = '' } = useParams();
  const { t, money, date } = useI18n();
  const { can } = useAuth();

  const { data: claim, isLoading } = claimsApi.useDetail(id);
  const [editOpen, setEditOpen] = React.useState(false);
  const [statusOpen, setStatusOpen] = React.useState(false);

  if (isLoading) return <DetailPageSkeleton />;
  if (!claim) return <EmptyState title={t('error.notFound')} hint={t('error.notFoundHint')} />;

  const claimRef = claim.claimNumber ?? claim.id.slice(0, 8);

  return (
    <DetailPage>
      <DetailHeader
        breadcrumbs={[{ label: t('claim.title'), to: '/claims' }, { label: claimRef }]}
        avatar={<EntityAvatar name={claim.insuranceCompanyName} size="lg" />}
        title={<span className="tabular">{claimRef}</span>}
        status={<EnumBadge enum="claimStatus" value={claim.status} />}
        meta={[
          { key: 'client', label: claim.clientName, to: `/clients/${claim.clientId}` },
          {
            key: 'policy',
            label: <span className="tabular">{claim.policyNumber}</span>,
            to: `/policies/${claim.policyId}`,
          },
          { key: 'company', label: claim.insuranceCompanyName },
        ]}
        actions={
          can('claim:write') && (
            <>
              <RequiresConnection reasonKey="offline.blocked.contract">
                <Button onClick={() => setStatusOpen(true)}>
                  <RefreshCw /> {t('claim.changeStatus')}
                </Button>
              </RequiresConnection>
              <Button variant="outline" onClick={() => setEditOpen(true)}>
                <Pencil /> {t('action.edit')}
              </Button>
            </>
          )
        }
      />

      <DetailMetrics>
        <Metric label={t('claim.amount')} value={money(claim.claimAmount)} />
        <Metric
          label={t('claim.payout')}
          value={money(claim.payoutAmount)}
          tone={claim.payoutAmount ? 'good' : undefined}
        />
        <Metric label={t('claim.incidentDate')} value={date(claim.incidentDate)} />
        <Metric
          label={t('claim.daysOpen')}
          value={claim.daysOpen ?? 0}
          tone={!claim.closedAt && (claim.daysOpen ?? 0) > 90 ? 'critical' : undefined}
        />
      </DetailMetrics>

      <Tabs defaultValue="details">
        <DetailTabsBar>
          <TabsList variant="underline">
            <TabsTrigger value="details">{t('common.details')}</TabsTrigger>
            <TabsTrigger value="documents">{t('common.documents')}</TabsTrigger>
          </TabsList>
        </DetailTabsBar>

        {/* The timeline is the claim's running history — supporting the case, not the
            case itself, so it rides in the aside exactly like the client's contact rail. */}
        <TabsContent value="details">
          <Workspace aside={<TimelinePanel claimId={id} />}>
            <SectionStack>
              <Section title={t('section.identity')}>
                <FieldGrid>
                  <Field label={t('claim.number')} mono>
                    {claim.claimNumber ?? '—'}
                  </Field>
                  <Field label={t('policy.one')}>
                    <Link to={`/policies/${claim.policyId}`} className="text-primary hover:underline">
                      <PolicyRef policyNumber={claim.policyNumber ?? ''} plates={claim.plates} muted />
                    </Link>
                  </Field>
                  <Field label={t('client.one')}>
                    <Link to={`/clients/${claim.clientId}`} className="text-primary hover:underline">
                      {claim.clientName}
                    </Link>
                  </Field>
                  <Field label={t('policy.company')}>{claim.insuranceCompanyName}</Field>
                  <Field label={t('client.agent')}>
                    {claim.assignedAgentName ?? t('common.unassigned')}
                  </Field>
                </FieldGrid>
              </Section>

              <Section title={t('section.incident')}>
                <FieldGrid>
                  <Field label={t('claim.incidentDate')} mono>
                    {date(claim.incidentDate)}
                  </Field>
                  <Field label={t('claim.reportDate')} mono>
                    {date(claim.reportDate)}
                  </Field>
                  <Field label={t('claim.daysOpen')} mono>
                    {claim.daysOpen ?? 0}
                  </Field>
                </FieldGrid>
                {claim.description && (
                  <p className="mt-5 text-[13.5px] leading-relaxed whitespace-pre-line">
                    {claim.description}
                  </p>
                )}
              </Section>

              <Section title={t('section.financial')}>
                <FieldGrid>
                  <Field label={t('claim.amount')} mono>
                    {money(claim.claimAmount)}
                  </Field>
                  <Field label={t('claim.payout')} mono>
                    {money(claim.payoutAmount)}
                  </Field>
                </FieldGrid>
              </Section>

              {claim.notes && (
                <Section title={t('section.notes')}>
                  <p className="text-[13.5px] leading-relaxed whitespace-pre-line">{claim.notes}</p>
                </Section>
              )}
            </SectionStack>
          </Workspace>
        </TabsContent>

        <TabsContent value="documents">
          <DocumentPanel scope="claims" ownerId={id} typeEnum="clientDocumentType" />
        </TabsContent>
      </Tabs>

      <ClaimFormDialog open={editOpen} onOpenChange={setEditOpen} claim={claim} />
      <StatusDialog claim={claim} open={statusOpen} onOpenChange={setStatusOpen} />
    </DetailPage>
  );
}

/** Spec §8.9: vertical timeline, system entries visually distinct from manual ones. */
function TimelinePanel({ claimId }: { claimId: string }) {
  const { t, te, dateTime } = useI18n();

  /**
   * A status-change entry is rebuilt from its metadata rather than shown as stored.
   *
   * The server writes the sentence when the change happens, so it is frozen in the
   * language and the raw enum value of that moment — «Η κατάσταση άλλαξε σε "closed"»,
   * untranslated, and still Greek when the user switches to English. The row already
   * carries `old_status`/`new_status`, so the UI can compose the sentence in the current
   * locale with proper labels. Older rows without metadata fall back to the stored text.
   */
  const describe = (entry: { actionType: string; description: string; metadata?: unknown }): string => {
    const meta = entry.metadata as { old_status?: string; new_status?: string } | null | undefined;
    if (entry.actionType === 'status_changed' && meta?.new_status) {
      return t('claim.statusChangedTo', {
        from: te('claimStatus', meta.old_status),
        to: te('claimStatus', meta.new_status),
      });
    }
    return entry.description;
  };
  const { can } = useAuth();
  const { data, isLoading } = useClaimTimeline(claimId);
  // A manual timeline note is commentary, not a status change — safe to queue.
  const addEntry = useOfflineMutation<Record<string, unknown>>({
    operation: 'claimTimeline.create',
    targetId: () => claimId,
    summary: (input) => String(input.description ?? ''),
    optimistic: (input, entryId) => ({
      queryKey: ['claims', 'detail', claimId, 'timeline'],
      record: {
        id: entryId,
        claimId,
        actionType: input.actionType,
        description: input.description,
        metadata: null,
        isSystem: false,
        createdBy: null,
        createdByName: null,
        createdAt: new Date().toISOString(),
        [PENDING_FLAG]: true,
      },
    }),
    onSuccessMessage: 'common.saved',
  });
  const actionOptions = useEnumOptions('timelineAction');

  const [actionType, setActionType] = React.useState('note_added');
  const [description, setDescription] = React.useState('');

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    await addEntry.mutate({ actionType, description });
    setDescription('');
  };

  return (
    <Section title={t('claim.timeline')}>
      <div>
        {can('claim:write') && (
          <form onSubmit={submit} className="mb-5 space-y-2">
            <Select value={actionType} onValueChange={setActionType}>
              <SelectTrigger size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {actionOptions
                  // System-generated kinds are written by the server, not by hand.
                  .filter((option) => !['created', 'status_changed', 'document_uploaded'].includes(option.value))
                  .map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Textarea
              rows={2}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={t('claim.addEntry')}
            />
            {/* Outline, not primary: the page's primary action is "change status" in
                the header. A second filled button in the rail would compete with it. */}
            <Button
              type="submit"
              variant="outline"
              size="sm"
              className="w-full"
              disabled={description.trim().length < 1}
              loading={addEntry.isPending}
            >
              {t('action.add')}
            </Button>
          </form>
        )}

        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : !data?.length ? (
          <EmptyState title={t('common.empty')} compact />
        ) : (
          <ol className="relative space-y-4 pl-6">
            {/* One continuous rail behind the markers reads as a single thread. */}
            <span className="bg-border absolute top-1 bottom-1 left-[0.6875rem] w-px" aria-hidden />

            {data.map((entry) => {
              const Icon = TIMELINE_ICONS[entry.actionType] ?? CircleDot;
              return (
                <li key={entry.id} className="relative">
                  {/* A manual entry gets the solid marker; a system one stays hollow, so
                      "what a person did" is separable from "what the system recorded"
                      without reading a single label. */}
                  <span
                    className={cn(
                      'bg-card absolute top-0.5 -left-6 grid size-6 place-items-center rounded-full border',
                      entry.isSystem
                        ? 'border-border text-muted-soft'
                        : 'border-foreground/15 text-foreground',
                    )}
                  >
                    <Icon className="size-3" />
                  </span>

                  <div className="min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <EnumBadge enum="timelineAction" value={entry.actionType} size="sm" />
                      {entry.isSystem && (
                        <Badge tone="outline" size="sm">
                          {t('claim.systemEntry')}
                        </Badge>
                      )}
                      {PENDING_FLAG in entry && <PendingSyncBadge />}
                    </div>
                    <p className="mt-1.5 text-[13px] leading-relaxed">{describe(entry)}</p>
                    <p className="text-muted-soft mt-1 text-[11px]">
                      {dateTime(entry.createdAt)}
                      {entry.createdByName ? ` · ${entry.createdByName}` : ''}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </Section>
  );
}

function StatusDialog({
  claim,
  open,
  onOpenChange,
}: {
  claim: { id: string; status: string; payoutAmount: string | null };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useI18n();
  const statusOptions = useEnumOptions('claimStatus');
  const changeStatus = useChangeClaimStatus(claim.id);

  const [status, setStatus] = React.useState(claim.status);
  const [note, setNote] = React.useState('');
  const [payoutAmount, setPayoutAmount] = React.useState(claim.payoutAmount ?? '');

  // The API requires a payout figure before a claim can be marked paid.
  const needsPayout = status === 'paid' && !claim.payoutAmount;

  const submit = async () => {
    await changeStatus.mutateAsync({
      status,
      note: note || undefined,
      payoutAmount: payoutAmount || undefined,
    });
    toast.success(t('common.saved'));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{t('claim.changeStatus')}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t('policy.status')}</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {needsPayout && (
            <div className="space-y-1.5">
              <Label htmlFor="payout">
                {t('claim.payout')} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="payout"
                type="number"
                step="0.01"
                className="tabular"
                value={payoutAmount}
                onChange={(event) => setPayoutAmount(event.target.value)}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="status-note">{t('common.notes')}</Label>
            <Textarea
              id="status-note"
              rows={3}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('action.cancel')}
          </Button>
          <Button
            disabled={needsPayout && !payoutAmount}
            loading={changeStatus.isPending}
            onClick={() => void submit()}
          >
            {t('action.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
